import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import { AppSettings } from "../../config/app-settings";
import {
  useLazyListCampaignQuery,
  useListCampaignQuery,
} from "../../redux/Api/CampaignManagment/cMCampaignsApiSlice";
import Loader from "../pages/loader";
import ReusableTable from "../../components/other/ReuseableTable";
import { can } from "../../helpers/auth";
import NoPermission from "../pages/noPermission";
import { toast } from "react-toastify";
import DownloadAllDataButton from "../../components/other/DownloadAllDataButton";
import SearchAndFilterBar from "../../components/other/search/SearchAndFilterBar";
import CustomersDropdown from "../../components/CustomersDropdown";

const CampaignsInfo = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});

  const {
    data: campaigns,
    isLoading,
    error,
    isFetching,
  } = useListCampaignQuery({
    expand: [
      "last_updated_by",
      "platforms",
      "campaign_type",
      "country",
    ].join(","),
    page: pagination.current,
    page_size: pagination.pageSize,
    ordering: "id",
    search: search || undefined,
    ...filters,
  });

  const columns = [
    { title: "ID", dataIndex: "id", key: "id", sorter: true },
    {
      title: "Campaign Name",
      dataIndex: "campaign_name",
      key: "campaign_name",
      filter: true,
      filterType: "text",
    },
    {
      title: "Asset ID",
      dataIndex: "asset_id",
      key: "asset_id",
      filter: true,
      filterType: "text",
    },
    {
      title: "Customers",
      dataIndex: "customer",
      key: "customer.customer_name",
      filter: true,
      filterComponent: CustomersDropdown,
      render: (_, record) => {
        const customerNames = record?.customer_names || [];
        const visibleCustomers = customerNames.slice(0, 2);
        const hiddenCustomers = customerNames.slice(2);

        if (customerNames.length === 0) {
          return "";
        }

        return (
          <div className="flex items-center gap-2 flex-wrap">
            {visibleCustomers.map((name) => (
              <button
                key={name}
                type="button"
                className="btn btn-outline-theme btn-sm !cursor-default !px-2 !py-0"
              >
                {name}
              </button>
            ))}

            {hiddenCustomers.length > 0 ? (
              <Tooltip title={hiddenCustomers.join(", ")}>
                <button
                  type="button"
                  className="btn btn-outline-theme btn-sm !cursor-default !px-2 !py-0"
                >
                  +{hiddenCustomers.length}
                </button>
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Campaign Release Date",
      dataIndex: "campaign_release_date",
      key: "campaign_release_date",
      filter: true,
      filterType: "date",
    },
    {
      title: "Campaign Status",
      dataIndex: "campaign_status",
      key: "campaign_status",
      filter: true,
      filterType: "select",
      filterOptions: [
        {
          label: "Active",
          value: true,
        },
        {
          label: "Inactive",
          value: false,
        },
      ],
      render: (text) => (text ? "Active" : "Inactive"),
    },
    {
      title: "Last Updated By",
      dataIndex: "last_updated_by",
      key: "last_updated_by",
      render: (text, record) => {
        const { last_updated_by } = record;
        return last_updated_by
          ? `${last_updated_by.first_name} ${last_updated_by.last_name}`
          : "";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <div className="flex gap-2">
          <button
            className="btn btn-outline-theme"
            onClick={() => handleEdit(record)}
          >
            Edit
          </button>
        </div>
      ),
    },
  ];

  const handleEdit = (record) => {
    sessionStorage.setItem("campaign_edit_step", 1);
    navigate(`/campaigns/edit/${record.id}`);
  };

  const handleTableChange = (paginationInfo) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    });
  };

  const context = useContext(AppSettings);
  useEffect(() => {
    context.setAppHeaderNone(false);
    context.setAppSidebarNone(false);
    context.setAppContentClass("");
    context.setAppThemePanal(true);

    return function cleanUp() {
      context.setAppHeaderNone(true);
      context.setAppSidebarNone(true);
      context.setAppContentClass("p-0");
      context.setAppThemePanal(false);
    };
  }, []);

  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat();
      errorMessages.forEach((message) => {
        toast.error(message);
      });
    }
  }, [error]);

  if (!can("view_campaign")) {
    return <NoPermission />;
  }
  return (
    <div className="w-full flex flex-col">
      <Loader isLoading={isLoading || isFetching} />
      <div className="flex mb-4">
        <SearchAndFilterBar
          columns={columns}
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setPagination={setPagination}
        />
        <div className="ml-auto flex gap-4">
          <DownloadAllDataButton
            queryParams={{
              expand: [
                "last_updated_by",
                "platforms",
                "campaign_type",
                "country",
              ].join(","),
            }}
            lazyQueryHook={useLazyListCampaignQuery}
            fileName="campaigns"
            transformData={{
              customer: (item) => (item.customer_names || []).join(", "),
              last_updated_by: (item) =>
                item.last_updated_by
                  ? `${item.last_updated_by.first_name} ${item.last_updated_by.last_name}`
                  : "",
              platforms: (item) =>
                item.platforms?.map((p) => p.platform_name).join(", "),
              campaign_type: (item) => item.campaign_type?.type ?? "",
              country: (item) => item.country?.country_name ?? "",
              campaign_status: (item) =>
                item.campaign_status ? "Active" : "Inactive",
            }}
          />
          <button
            className="btn btn-outline-theme ml-auto mb-4"
            onClick={() => {
              sessionStorage.setItem("campaign_edit_step", 1);
              navigate("/campaigns/create");
            }}
          >
            Add New Campaign
          </button>
        </div>
      </div>
      <br />
      {campaigns ? (
        <ReusableTable
          columns={columns}
          data={campaigns?.results}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: campaigns?.count,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      ) : null}
    </div>
  );
};

export default CampaignsInfo;
